import { useNavigate } from "react-router-dom";
import { LEGAL, LEGAL_LINKS } from "@/lib/legal";

const SECTIONS = [
  {
    id: "intro",
    title: "تمهيد وطبيعة الاتّفاق",
    body: (
      <>
        <p>
          تُنَظِّم هذه الشروط (وهي تُشَكِّل «اتفاقيّة ترخيص المستخدم النهائيّ»
          — EULA) العلاقة بينك وبين {LEGAL.entity_ar} ({LEGAL.entity_en}) —
          السجلّ التجاريّ رقم {LEGAL.cr_number} — الجهة المُشَغِّلة لتطبيق
          «{LEGAL.app_name_ar}». باستخدامك التطبيق — سواءٌ بالتسجيل أو
          بصفة زائر أو بعد شراء اشتراك — فإنّك تُوافق على الالتزام بهذه
          الشروط بالكامل. إن لم تكن موافقًا على أيّ بندٍ منها، فيُرجى
          التوقُّف عن استخدام التطبيق فورًا.
        </p>
        <p style={{ fontSize: 13.5 }} className="muted">
          مُلاحظة لمستخدمي iOS: تُطبَّق أيضًا اتفاقيّة الترخيص الافتراضيّة لـ Apple،
          وفي حال تعارُضٍ بين البنود يُطبَّق الأشدّ حمايةً لحقوق المستخدم.
          {" "}
          <a href={LEGAL_LINKS.apple_eula} target="_blank" rel="noreferrer" className="legal-link">
            (Apple Standard EULA)
          </a>
        </p>
      </>
    ),
  },
  {
    id: "educational-use",
    title: "الاستخدام للأغراض التعليميّة",
    body: (
      <>
        <p>
          تطبيق «{LEGAL.app_name_ar}» مُخصَّصٌ حصرًا للأغراض التعليميّة الفرديّة والمؤسّسيّة
          لتعلّم البلاغة العربيّة ومهاراتها. لا يجوز استخدامه:
        </p>
        <ul className="about-list">
          <li>لأيّ غرضٍ تجاريٍّ دون موافقةٍ خطّيّةٍ مُسبَقة.</li>
          <li>لأيّ نشاطٍ مخالفٍ للأنظمة والقوانين السارية في {LEGAL.jurisdiction_ar}.</li>
          <li>بطريقةٍ تُسيء إلى التطبيق أو إلى المستخدمين الآخرين.</li>
          <li>لاختبار أنظمة الذكاء الاصطناعيّ بنصوصٍ مسيئة أو مُحَرِّضة أو منتهكةٍ للقانون.</li>
        </ul>
      </>
    ),
  },
  {
    id: "subscriptions",
    title: "الاشتراكات المُتَجَدِّدة تلقائيًّا",
    body: (
      <>
        <p style={{ fontWeight: 600 }}>
          يوفّر التطبيق اشتراكاتٍ تدفع عبر App Store الخاصّة بـ Apple و Google Play
          الخاصّة بـ Google. جميع الاشتراكات <u>تتجدّد تلقائيًّا</u> ما لم تُلغِها
          قبل 24 ساعة من نهاية الفترة الحاليّة.
        </p>
        <ul className="about-list">
          <li>
            <strong>مدد الاشتراك المتاحة:</strong> شهريّة، أو سنويّة (يمكنك تغيير الخطّة
            من إعدادات الاشتراكات في متجر Apple / Google).
          </li>
          <li>
            <strong>الفوترة:</strong> يُحصَّل مبلغ الاشتراك عبر حساب Apple ID / Google
            الخاصّ بك عند تأكيد الشراء، ثمّ يتجدّد تلقائيًّا عند بداية كلّ فترةٍ جديدة
            بنفس المبلغ ما لم يُغَيَّر السعر مع إخطارٍ مُسبَقٍ منّا.
          </li>
          <li>
            <strong>الإلغاء:</strong> يمكنك إلغاء التجديد التلقائيّ في أيّ وقتٍ من
            «إعدادات الاشتراكات» في جهازك (Settings ← Apple ID ← Subscriptions لأجهزة
            iOS، أو Play Store ← Subscriptions لأجهزة Android). عند الإلغاء تحتفظ
            بالوصول حتّى نهاية الفترة المدفوعة.
          </li>
          <li>
            <strong>لا استرداد جزئيّ:</strong> بمجرّد إتمام الشراء لا تُستَرَدّ رسوم الفترة
            الحاليّة، إلّا وفقًا لسياسة الاسترداد الرسميّة لِـ Apple / Google.
          </li>
          <li>
            <strong>تغيير الأسعار:</strong> عند رفع السعر سنُخطِرُك مُسبَقًا وسيَطلُب المتجر
            موافقَتَك الصريحة قبل التجديد التالي وفقًا لسياسة كلّ متجر.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "cancellation",
    title: "كيفيّة إلغاء الاشتراك",
    body: (
      <ol className="about-list">
        <li>
          <strong>على iOS/iPadOS:</strong> Settings ← [اسمك] ← Subscriptions ← اختر
          «{LEGAL.app_name_ar}» ← Cancel Subscription.
        </li>
        <li>
          <strong>على Android:</strong> Play Store ← الحساب ← Payments &amp; subscriptions ←
          Subscriptions ← «{LEGAL.app_name_ar}» ← Cancel subscription.
        </li>
        <li>
          <strong>من داخل التطبيق:</strong> افتح «حسابي» ثمّ اضغط «إدارة الاشتراك» —
          يُنقَلك إلى شاشة الإلغاء الخاصّة بالمتجر مُباشَرةً.
        </li>
      </ol>
    ),
  },
  {
    id: "ip",
    title: "حماية الملكيّة الفكريّة",
    body: (
      <p>
        جميع المحتوى التعليميّ والدروس والتصاميم والشعارات والعلامة التجاريّة
        «{LEGAL.app_name_ar}» محميّةٌ بموجب قوانين الملكيّة الفكريّة في {LEGAL.jurisdiction_ar}
        والاتفاقيّات الدوليّة. أيّ انتهاكٍ يُعرِّض المُنتَهِك للمساءلة القانونيّة. لمزيدٍ من التفاصيل
        راجع{" "}
        <a className="legal-link" href={LEGAL_LINKS.ip}>صفحة الملكيّة الفكريّة</a>.
      </p>
    ),
  },
  {
    id: "user-content",
    title: "المحتوى الذي يُنشِئه المستخدم",
    body: (
      <ul className="about-list">
        <li>
          النصوص التي تكتبها داخل التطبيق تبقى ملكًا لك، لكنَّك تمنحنا ترخيصًا غير
          حصريٍّ ومَجّانيًّا وقابلًا للإلغاء لمعالجتها بهدف تقديم خدمات التقييم
          والمحادثة بالذكاء الاصطناعيّ.
        </li>
        <li>
          يجب أن يكون المحتوى الذي تكتبه ملائمًا لبيئةٍ تعليميّة، وخاليًا من الإساءة
          أو الانتهاكات أو المحتوى الجنسيّ الفاضح أو تحريض العُنف.
        </li>
        <li>
          نحتفظ بحقّ حذف أيّ محتوى يُخالف هذه الشروط، وإبلاغ الجهات المُختصَّة إن
          استدعى الأمر ذلك.
        </li>
      </ul>
    ),
  },
  {
    id: "account-suspension",
    title: "تعليق الحساب عند سوء الاستخدام",
    body: (
      <ul className="about-list">
        <li>انتهاك أيٍّ من هذه الشروط.</li>
        <li>الاشتباه بنشاطٍ احتياليٍّ أو محاولات هندسةٍ عكسيّة.</li>
        <li>إساءة استخدام معلّم الذكاء الاصطناعيّ بنصوصٍ مسيئة أو انتهاكيّة.</li>
        <li>محاولة استغلال ثغراتٍ تقنيّة أو الإضرار بالخدمة أو مستخدميها.</li>
      </ul>
    ),
  },
  {
    id: "ai-disclaimer",
    title: "إخلاء المسؤوليّة عن مخرجات الذكاء الاصطناعيّ",
    body: (
      <p>
        يستخدم التطبيق نماذج ذكاءٍ اصطناعيٍّ لتقييم النصوص ومحاورتك. وعلى الرغم من
        جودة هذه النماذج، فإنّها قد تُخطئ أو تُقدِّم تقييماتٍ غير دقيقة. تقييماتها
        إرشاديّة، ولا تُعَدّ بديلًا عن المعلّم البشريّ المُؤَهَّل ولا مشورةً أكاديميّةً
        رسميّة. نحن غير مسؤولين عن أيّ قراراتٍ تتَّخِذُها بناءً على مخرجات الذكاء
        الاصطناعيّ.
      </p>
    ),
  },
  {
    id: "service-availability",
    title: "توافر الخدمة",
    body: (
      <p>
        نسعى لتوفير الخدمة بشكلٍ مستمرّ، لكنَّنا لا نضمن خلوَّها من الأعطال أو
        الانقطاعات. نحتفظ بحقّ تعديل أو إيقاف أيّ ميزةٍ من ميزات التطبيق في أيّ
        وقت، مع إشعار المستخدمين عند الإمكان. للمشتركين النَشِطين، سيُلغى الاشتراك
        وتُعاد فَوترة أيّ فترةٍ لم يُستَخدَم فيها التطبيق بسبب إيقافٍ جوهريٍّ من
        جانبنا وفقًا لسياسة الاسترداد لدى Apple / Google.
      </p>
    ),
  },
  {
    id: "warranty",
    title: "إخلاء الضمان وحدود المسؤوليّة",
    body: (
      <p>
        يُقَدَّم التطبيق «كما هو» و«حَسب توفُّرِه» دون أيّ ضماناتٍ صريحةٍ أو ضمنيّة.
        إلى الحدّ الذي يُجيزه القانون، لا تتحمّل {LEGAL.entity_ar} أيّ مسؤوليّةٍ عن
        الأضرار العَرَضيّة أو التبعيّة أو خسارة الأرباح النَاجمة عن استخدامك التطبيق،
        وفي جميع الأحوال لا يتجاوز إجماليّ مسؤوليَّتنا قيمة المبلغ الذي دفعتَه لنا
        خلال الاثنَي عشر شهرًا السابقة للحادثة.
      </p>
    ),
  },
  {
    id: "termination",
    title: "الإنهاء",
    body: (
      <p>
        يمكنك إنهاء هذا الاتّفاق في أيّ وقتٍ بحذف حسابك من داخل التطبيق أو مراسلتنا.
        نحتفظ بحقّ إنهاء الاتّفاق في حالات المخالفات الجسيمة أو النشاط الاحتيالي.
        بعد الإنهاء تبقى بنودُ الملكيّة الفكريّة وحدود المسؤوليّة سارية.
      </p>
    ),
  },
  {
    id: "future-updates",
    title: "تحديث الشروط",
    body: (
      <p>
        قد نُحدِّث هذه الشروط من حينٍ لآخر، وسوف نعرض النسخة المُحدَّثة في هذه الصفحة
        مع تاريخ التحديث. عند وجود تعديلاتٍ جوهريّة تُؤثِّر في حقوقك، سنُخطِرُك داخل
        التطبيق قبل سريان التغيير.
        {" "}<strong>استمرارك في استخدام التطبيق بعد التحديث يُعَدّ موافقةً ضمنيّةً على
        الشروط الجديدة</strong>.
      </p>
    ),
  },
  {
    id: "law",
    title: "القانون المُطَبَّق والاختصاص القضائيّ",
    body: (
      <p>
        تخضع هذه الشروط لأنظمة {LEGAL.jurisdiction_ar}. أيّ نزاعٍ ينشأ عن استخدام
        التطبيق يُحلّ ودّيًّا بداءةً، أو أمام المحاكم المختصّة في {LEGAL.city_ar} عند
        تعذُّر ذلك. لا يُخِلّ هذا البند بحقوقك القانونيّة غير القابلة للتنازُل
        بموجب قوانين بلد إقامتك.
      </p>
    ),
  },
  {
    id: "contact",
    title: "للتواصل بشأن الشروط",
    body: (
      <p>
        لأيّ استفسارٍ يتعلّق بهذه الشروط:{" "}
        <a
          href={`mailto:${LEGAL.support_email}`}
          className="legal-link"
          data-testid="terms-contact-email"
          dir="ltr"
        >
          {LEGAL.support_email}
        </a>
        <br />
        {LEGAL.entity_ar} — {LEGAL.address_ar} — س.ت: {LEGAL.cr_number}
      </p>
    ),
  },
];

export default function TermsPage() {
  const navigate = useNavigate();
  return (
    <div className="shell has-bottom-nav legal-page" data-testid="terms-page">
      <div className="page-header legal-hero">
        <div className="legal-badge">شروط الاستخدام</div>
        <h1 data-testid="terms-title">
          شروط <span className="gold">الاستخدام</span>
        </h1>
        <p>
          نُوضِّح هنا حقوق المستخدم والتزاماته، لضمان تجربةٍ تعليميّةٍ آمنةٍ ومثمرةٍ
          للجميع.
        </p>
        <div className="legal-meta" data-testid="terms-updated">
          آخر تحديث: {LEGAL.last_updated}
        </div>
      </div>

      {SECTIONS.map((s) => (
        <section
          key={s.id}
          className="about-section legal-section"
          data-testid={`terms-section-${s.id}`}
        >
          <h2>{s.title}</h2>
          <div className="legal-body">{s.body}</div>
        </section>
      ))}

      <div className="about-cta">
        <button
          className="btn btn-primary"
          onClick={() => navigate("/")}
          data-testid="terms-back-btn"
          style={{ padding: "12px 24px" }}
        >
          العودة إلى الرئيسيّة
        </button>
        <button
          className="btn btn-ghost"
          onClick={() => navigate("/privacy")}
          data-testid="terms-to-privacy"
        >
          سياسة الخصوصيّة
        </button>
      </div>
    </div>
  );
}
